import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Boxes, Menu, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { Coins, Key, Plugs, Robot, UsersThree } from "@phosphor-icons/react";

import { getBillingAccount, type BillingAccount, type Me } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/tooltip";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { UserMenu } from "@/components/UserMenu";
import { AgentNav } from "@/components/agentSettings";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/agents", label: "Agents", icon: Robot },
  { to: "/servers", label: "Integrations", icon: Plugs },
  { to: "/secrets", label: "Secrets", icon: Key },
  { to: "/credits", label: "Credits", icon: Coins },
  { to: "/members", label: "Members", icon: UsersThree },
];

// CreditBanner is the app-wide nudge shown when the workspace is out of (or low
// fmtCredits renders a whole-credit count compactly for the sidebar.
function fmtCredits(n: number): string {
  return Math.round(n).toLocaleString();
}

// CreditMeter is the sidebar's at-a-glance balance: available credits + a thin
// gauge (spendable / cap), tappable → /credits. Tone shifts amber when low and
// red when blocked. Renders nothing if billing is off / unavailable.
function CreditMeter({ acct, onNavigate }: { acct: BillingAccount | null; onNavigate?: () => void }) {
  if (!acct) return null;
  const pct =
    acct.cap_credits > 0 ? Math.max(2, Math.min(100, (acct.spendable_credits / acct.cap_credits) * 100)) : 0;
  const text = acct.blocked ? "text-destructive" : acct.low_balance ? "text-warning" : "text-foreground";
  const bar = acct.blocked ? "bg-destructive" : acct.low_balance ? "bg-warning" : "bg-primary";
  return (
    <Link
      to="/credits"
      onClick={onNavigate}
      className="block rounded-lg border bg-background/40 px-3 py-2.5 transition-colors hover:bg-accent"
      title="View credits"
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Coins className="h-3.5 w-3.5" /> Credits
        </span>
        {acct.plan_name && (
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {acct.plan_name}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-baseline gap-1">
        <span className={cn("text-lg font-semibold tabular-nums", text)}>{fmtCredits(acct.spendable_credits)}</span>
        {acct.cap_credits > 0 && (
          <span className="text-xs text-muted-foreground">/ {fmtCredits(acct.cap_credits)}</span>
        )}
      </div>
      {acct.cap_credits > 0 && (
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div className={cn("h-full rounded-full transition-all", bar)} style={{ width: `${pct}%` }} />
        </div>
      )}
    </Link>
  );
}

// on) credits. Dismissible per session. Renders nothing when fine/unavailable.
function CreditBanner({ acct }: { acct: BillingAccount | null }) {
  const [dismissed, setDismissed] = useState(false);
  if (!acct || dismissed || (!acct.blocked && !acct.low_balance)) return null;
  const blocked = acct.blocked;
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 border-b px-4 py-2 text-sm sm:px-6 lg:px-10",
        blocked ? "border-destructive/30 bg-destructive/10 text-destructive" : "border-warning/40 bg-warning/10 text-warning",
      )}
    >
      <span>
        {blocked
          ? "Your workspace is out of credits — agents are paused."
          : "Your credit balance is running low."}{" "}
        <Link to="/credits" className="font-medium underline underline-offset-2">
          View credits
        </Link>
      </span>
      <button onClick={() => setDismissed(true)} aria-label="Dismiss" className="shrink-0 opacity-70 hover:opacity-100">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function Layout({ me, onSignedOut, children }: { me: Me; onSignedOut: () => void; children: ReactNode }) {
  const workspace = me.workspaces?.find((w) => w.tenant_id === me.current_tenant_id);
  const [mobileOpen, setMobileOpen] = useState(false);
  const close = () => setMobileOpen(false);
  // Desktop-only icon rail: collapse the sidebar to icons (with hover tooltips) to
  // give the main surface — especially the agent chat — more width. Persisted; the
  // collapse visuals are all lg:-gated so the mobile drawer stays full-label.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("cb-nav-collapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleCollapsed = () =>
    setCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem("cb-nav-collapsed", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  // One billing fetch shared by the sidebar meter + the low/blocked banner.
  const [acct, setAcct] = useState<BillingAccount | null>(null);
  useEffect(() => {
    getBillingAccount()
      .then(setAcct)
      .catch(() => setAcct(null));
  }, []);
  // The agent workspace (/agents/<id>) is a chat-first, full-height surface: it
  // bleeds past the standard padded, max-width content wrapper so the conversation
  // fills the viewport. Every other route keeps the centered page layout.
  const { pathname } = useLocation();
  // Inside an agent (/agents/<id> and its sub-routes, but not the list or the
  // create page), the sidebar swaps its middle nav for the agent's sections +
  // Back/Chat — same shell chrome, different menu.
  const agentMatch = pathname.match(/^\/agents\/([^/]+)/);
  const agentId = agentMatch && agentMatch[1] !== "new" ? agentMatch[1] : null;
  // The bare agent chat AND the Workspace section bleed (full-height, no padded
  // wrapper) — both are app-like editing surfaces. Other settings, forms, the
  // agents list and /agents/new keep the centered page layout.
  const bleed =
    !!agentId && (pathname === `/agents/${agentId}` || pathname === `/agents/${agentId}/settings/workspace`);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile top bar */}
      <header className="fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between border-b bg-card/80 px-3 backdrop-blur lg:hidden">
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2 text-sm font-semibold">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-[#A6ABFF] to-[#7b82f0] text-white">
            <Boxes className="h-4 w-4" />
          </div>
          CodeBridge
        </div>
        <ThemeToggle className="h-9 w-9" />
      </header>

      {/* Drawer backdrop (mobile only) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={close} aria-hidden />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex h-screen w-64 shrink-0 flex-col border-r bg-card transition-transform duration-200 lg:sticky lg:top-0 lg:z-auto lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          collapsed && "lg:w-16",
        )}
      >
        <div className={cn("flex items-center gap-2.5 px-4 py-4", collapsed && "lg:justify-center lg:px-2")}>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#A6ABFF] to-[#7b82f0] text-white">
            <Boxes className="h-5 w-5" />
          </div>
          <div className={cn("min-w-0 flex-1", collapsed && "lg:hidden")}>
            <div className="text-sm font-semibold leading-tight">CodeBridge</div>
            <div className="truncate text-xs text-muted-foreground">{workspace?.name ?? me.current_tenant_id}</div>
          </div>
          <ThemeToggle className={cn("hidden h-8 w-8 shrink-0 lg:flex", collapsed && "lg:hidden")} />
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 lg:hidden" onClick={close} aria-label="Close menu">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {agentId ? (
          <AgentNav projectId={agentId} onNavigate={close} collapsed={collapsed} />
        ) : (
          <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
            {NAV.map(({ to, label, icon: Icon }) => (
              <Tooltip key={to} label={label} disabled={!collapsed}>
                <NavLink
                  to={to}
                  onClick={close}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      collapsed && "lg:justify-center lg:gap-0 lg:px-2",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground",
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" /> <span className={cn(collapsed && "lg:hidden")}>{label}</span>
                </NavLink>
              </Tooltip>
            ))}
          </nav>
        )}

        <div className="space-y-2 border-t p-2">
          <div className={cn(collapsed && "lg:hidden")}>
            <CreditMeter acct={acct} onNavigate={close} />
          </div>
          <UserMenu me={me} onSignedOut={onSignedOut} collapsed={collapsed} />
          <button
            type="button"
            onClick={toggleCollapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(
              "hidden w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground lg:flex",
              collapsed && "lg:justify-center lg:gap-0 lg:px-2",
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4 shrink-0" />
            ) : (
              <>
                <PanelLeftClose className="h-4 w-4 shrink-0" /> <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {bleed ? (
        <main className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden pt-14 lg:pt-0">{children}</main>
      ) : (
        <main className="scrollbar-thin min-w-0 flex-1 overflow-auto pt-14 lg:pt-0">
          <CreditBanner acct={acct} />
          <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-10 lg:py-8">{children}</div>
        </main>
      )}
    </div>
  );
}
